import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getEventService,
  getContractService,
  getEventTypesService,
  getRoomTemplateService,
  getRoomTemplatesService,
} from "../../../services/commonService";
import { getDeploymentService } from "../../../services/deploymentServices";
import { buildLarixUrl } from "../../../utils/larix-url";
import { useQuery } from "components/graphql";
import { getGraphicSetQueryWithAssets } from "../../graphicset/details-page/query";
import getAssetQuery from "../../asset/details-page/query";
import getEventEvuQuery from "./evu-query";
import getEventUsersQuery from "./event-users-query";

const POLL_INTERVAL_MS = 5000;

// GraphQL siempre devuelve `null` para un campo ausente; el JSON REST simplemente omite la
// clave, dando `undefined` en JS. Varios componentes de la página renderizan con patrones
// `return a && b && (<jsx>)`, y React lanza un Invariant Violation si ese `&&` termina
// devolviendo `undefined` (a diferencia de `null`/`false`, que sí están permitidos). Por eso
// aquí se normaliza explícitamente cada valor con `?? null` en vez de dejarlo `undefined`.
const orNull = value => value ?? null;

const mapComponentStatus = componentStatus =>
  (componentStatus || []).map(item => ({
    name: orNull(item.name),
    // el swagger documenta el estado como "info", pero el backend/mocks también lo envían como "status"
    status: orNull(item.info ?? item.status),
    autorun: orNull(item.autorun),
    stereotype: orNull(item.stereotype),
    published: item.published
      ? {
          hls: orNull(item.published.hls),
          live: orNull(item.published.live),
          youtube: orNull(item.published.youtube),
          viewUrl: orNull(item.published.view_url),
          debug: orNull(item.published.debug),
        }
      : null,
  }));

const mapVwsCamList = vwsCamList =>
  (vwsCamList || []).map(cam => ({
    id: orNull(cam.id),
    name: orNull(cam.name),
    type: orNull(cam.type),
    src: orNull(cam.src),
  }));

const mapRemoteIngestions = remoteIngestions =>
  (remoteIngestions || []).map(item => ({
    id: String(item.remote_ingestion_id),
    name: orNull(item.remote_ingestion_name),
    type: orNull(item.remote_ingestion_type),
    url: orNull(item.remote_ingestion_url),
    // el REST no precalcula larixUrl como sí hace el resolver GraphQL
    larixUrl: buildLarixUrl(item.remote_ingestion_type, item.remote_ingestion_url),
  }));

const mapRecordingList = recordingList =>
  (recordingList || []).map(item => ({
    main: orNull(item.is_main_recording),
    url: orNull(item.recording_url),
    status: orNull(item.recording_status),
    description: orNull(item.recording_description),
  }));

const mapInfoBert = infoBert =>
  infoBert
    ? {
        componentAnyAbnormal: orNull(infoBert.component_any_abnormal),
        elementAnyAbnormal: orNull(infoBert.element_any_abnormal),
        iaDebugUrl: orNull(infoBert.ia_debug_url),
        iaScoreDebugUrl: orNull(infoBert.ia_score_debug_url),
        liveViewUrl: orNull(infoBert.live_view_url),
        remoteIngestions: mapRemoteIngestions(infoBert.remote_ingestions),
        recordingList: mapRecordingList(infoBert.recording_list),
        componentStatus: mapComponentStatus(infoBert.component_status),
        vwsCamList: mapVwsCamList(infoBert.vws_cam_list),
      }
    : null;

const mapLiveStreams = liveStreams =>
  (liveStreams || []).map(stream => ({
    rtmpUrl: orNull(stream.rtmp_url),
    key: orNull(stream.key),
    publishingUrl: orNull(stream.publishing_url),
    description: orNull(stream.description),
    componentName: orNull(stream.component_name),
    componentStatus: orNull(stream.component_status),
    type: orNull(stream.type),
  }));

// teamLogoAsset1/2 llegan ya en camelCase desde GraphQL (pages/asset/details-page/query.js),
// no hace falta traducir snake_case como con el resto de campos REST.
const mapSportEventData = (sportEventData, { teamLogoAsset1, teamLogoAsset2 } = {}) =>
  sportEventData
    ? {
        scoreboardTemplateId: orNull(sportEventData.alt_scoreboard_template_id),
        scoreboardType: orNull(sportEventData.scoreboard_type),
        altScoreboardTemplateName: orNull(sportEventData.alt_scoreboard_template_name),
        teamName1Abreviated: orNull(sportEventData.team_name_1_abreviated),
        teamName1: orNull(sportEventData.team_name_1),
        teamLogo1: orNull(sportEventData.team_logo_1),
        teamLogoAsset1: teamLogoAsset1 ?? null,
        teamName2Abreviated: orNull(sportEventData.team_name_2_abreviated),
        teamName2: orNull(sportEventData.team_name_2),
        teamLogo2: orNull(sportEventData.team_logo_2),
        teamLogoAsset2: teamLogoAsset2 ?? null,
      }
    : null;

const mapMatchPointConfig = matchPointConfig =>
  matchPointConfig
    ? {
        ocurrencesSource: orNull(matchPointConfig.ocurrences_source),
        ocurrencesSourceId: orNull(matchPointConfig.ocurrences_source_id),
        ocurrencesVisible: orNull(matchPointConfig.ocurrences_visible),
        matchPointUrl: orNull(matchPointConfig.match_point_url),
      }
    : null;

const mapLocalRecInfo = localRecInfo =>
  localRecInfo
    ? {
        downloadWebUrl: orNull(localRecInfo.download_web_url),
        userEmails: localRecInfo.user_emails || [],
      }
    : null;

const mapSubdeployment = subdeployment =>
  subdeployment
    ? {
        name: orNull(subdeployment.name),
        description: orNull(subdeployment.description),
        streamsourceIds: subdeployment.streamsource_ids || [],
      }
    : null;

// startedByUser/finishedByUser llegan ya en camelCase y planos desde GraphQL
// (event-users-query.js): no hace falta traducir la forma anidada que tenía REST.
const mapUser = user =>
  user
    ? {
        name: orNull(user.name),
        username: orNull(user.username),
      }
    : null;

// graphicSet llega ya en camelCase desde GraphQL (pages/graphicset/details-page/query.js).
const mapGraphicSet = (graphicSetId, graphicSet) => {
  if (!graphicSetId) {
    return null;
  }
  return {
    id: graphicSetId,
    name: orNull(graphicSet?.name),
    customerId: orNull(graphicSet?.customerId),
    layouts: (graphicSet?.layouts || []).map(layout => ({
      assetId: orNull(layout.assetId),
    })),
  };
};

/**
 * Traduce la respuesta cruda de getEventService (snake_case) al mismo shape camelCase
 * que producía la query GraphQL `Event` (details-page/query.js), para que ningún
 * componente hijo de la página de detalle tenga que cambiar.
 *
 * El resto de argumentos son datos auxiliares resueltos aparte (equivalentes a los nested
 * resolvers de GraphQL `contract`, `deployment`, `type`, `roomTemplate`, `startedByUser`,
 * `finishedByUser`, `graphicSet` y `evu`), porque el REST del evento no los trae embebidos.
 */
export const mapEventFromRest = (
  raw,
  {
    contract,
    deployment,
    eventType,
    roomTemplate,
    hasRoomTemplates,
    startedByUser,
    finishedByUser,
    graphicSet,
    evu,
    teamLogoAsset1,
    teamLogoAsset2,
  } = {},
) => {
  if (!raw) {
    return null;
  }

  return {
    id: orNull(raw._id),
    title: orNull(raw.event?.title),
    description: orNull(raw.event?.description),
    createdAt: orNull(raw.created_at),
    deletedBy: orNull(raw.deleted_by),
    deletedAt: orNull(raw.deleted_at),
    billable: orNull(raw.billable),
    visible: orNull(raw.visible),
    isInternalTest: orNull(raw.is_internal_test),
    localRec: orNull(raw.local_rec),
    activeQod: orNull(raw.active_qod),
    isMutedOnStart: orNull(raw.is_muted_on_start),
    unmuteMinutesBefore: orNull(raw.unmute_minutes_before),
    localRecInfo: mapLocalRecInfo(raw.local_rec_info),
    authorizedRecording: orNull(raw.authorized_recording),
    typeId: orNull(raw.event_type_id),
    graphicSetId: orNull(raw.graphic_set_id),
    customerId: orNull(contract?.customer_id),
    roomTemplateId: orNull(raw.room_template_id),
    contractId: orNull(raw.service_contract_id),
    remoteProductionAvailable: orNull(raw.remote_production_available),
    bertConfigId: orNull(raw.bert_config_id),
    bertSimpleUrl: orNull(raw.bert_simple_url),
    emailReceipts: raw.email_receipts || [],
    emailLocale: raw.email_locale || [],
    publicEventId: orNull(raw.public_event_id),
    subdeployment: mapSubdeployment(raw.subdeployment),
    startedByUser: mapUser(startedByUser),
    finishedByUser: mapUser(finishedByUser),
    evu: evu ?? null,
    contract: contract
      ? {
          id: orNull(contract._id),
          description: orNull(contract.description),
          hasRoomTemplates: !!hasRoomTemplates,
          deployment: deployment
            ? {
                id: orNull(deployment._id),
                codename: orNull(deployment.codename),
                sourceIds: deployment.streamsource_ids || [],
              }
            : null,
        }
      : null,
    graphicSet: mapGraphicSet(raw.graphic_set_id, graphicSet),
    type: eventType
      ? {
          description: orNull(eventType.description),
          isSportEvent: orNull(eventType.is_sport_event),
        }
      : null,
    keywords: raw.keywords || [],
    // El REST devuelve start_date/end_date en segundos (igual que el resto de fechas de esta
    // API, ver p.ej. item.event.start_date * 1000 en pages/event/table/table.jsx). GraphQL daba
    // aquí un objeto Date real (vía su escalar Calendar), no un número: los formularios de
    // edición validan estos campos con yup.date(), y esta versión de yup (0.27) solo sabe
    // convertir un Date o un string ISO, no un timestamp en milisegundos (lanza "could not be
    // cast" si se le pasa un número). Por eso hay que construir el Date aquí, no dejarlo en number.
    startDate: raw.event?.start_date ? new Date(raw.event.start_date * 1000) : null,
    endDate: raw.event?.end_date ? new Date(raw.event.end_date * 1000) : null,
    eventStatus: orNull(raw.event_status),
    urlRemoteProduction: orNull(raw.url_remote_production),
    urlRemoteLocution: orNull(raw.url_remote_locution),
    urlRemoteLocutionShort: orNull(raw.url_remote_locution_short),
    urlMixerBert: orNull(raw.url_mixer_bert),
    elasticIp: orNull(raw.elastic_ip),
    plenarySessionUrl: orNull(raw.plenary_session_url),
    roomTemplate: roomTemplate
      ? {
          test: orNull(roomTemplate.test),
          codename: orNull(roomTemplate.codename),
        }
      : null,
    liveStreams: mapLiveStreams(raw.livestreams),
    sportEventData: mapSportEventData(raw.sport_event_data, { teamLogoAsset1, teamLogoAsset2 }),
    matchPointConfig: mapMatchPointConfig(raw.match_point_config),
    infoBert: mapInfoBert(raw.info_bert),
  };
};

const fetchContract = contractId =>
  new Promise((resolve, reject) => {
    if (!contractId) {
      resolve(null);
      return;
    }
    getContractService(contractId, resolve, reject);
  });

const fetchDeployment = deploymentId =>
  new Promise((resolve, reject) => {
    if (!deploymentId) {
      resolve(null);
      return;
    }
    getDeploymentService({ deploymentId }, resolve, reject);
  });

const fetchEventType = eventTypeId =>
  new Promise(resolve => {
    if (!eventTypeId) {
      resolve(null);
      return;
    }
    getEventTypesService(eventTypeId, resolve);
  });

const fetchRoomTemplate = (contractId, roomTemplateId) =>
  new Promise((resolve, reject) => {
    if (!contractId || !roomTemplateId) {
      resolve(null);
      return;
    }
    getRoomTemplateService(contractId, roomTemplateId, resolve, reject);
  });

const fetchHasRoomTemplates = contractId =>
  new Promise(resolve => {
    if (!contractId) {
      resolve(false);
      return;
    }
    getRoomTemplatesService(contractId, true, list => resolve(!!list?.length), () => resolve(false));
  });

/**
 * Hook REST equivalente a useQuery(getEventQuery(...)), con el mismo polling de 5s.
 * Devuelve { event, loading, error, contract, deployment } con la misma semántica que
 * consumía QueryResponseHandler (`contract`/`deployment` crudos se exponen aparte para que
 * otras pantallas, como la de editar, los reutilicen sin duplicar la petición).
 */
export const useEventDetailsRest = ({ eventId, withEvu = false }) => {
  const [rawEvent, setRawEvent] = useState(null);
  const [contract, setContract] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [eventType, setEventType] = useState(null);
  const [roomTemplate, setRoomTemplate] = useState(null);
  const [hasRoomTemplates, setHasRoomTemplates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshEvent = useCallback(() => setRefreshTick(tick => tick + 1), []);

  useEffect(
    () => {
      if (!eventId) {
        return undefined;
      }

      let cancelled = false;

      const fetchEvent = () => {
        getEventService(
          eventId,
          data => {
            if (cancelled) return;
            setRawEvent(data);
            setError(null);
            setLoading(false);
          },
          err => {
            if (cancelled) return;
            setError(err);
            setLoading(false);
          },
        );
      };

      fetchEvent();
      const intervalId = setInterval(fetchEvent, POLL_INTERVAL_MS);

      return () => {
        cancelled = true;
        clearInterval(intervalId);
      };
    },
    [eventId, refreshTick],
  );

  const contractId = rawEvent?.service_contract_id;
  const deploymentId = contract?.deployment_id;
  const customerId = contract?.customer_id;
  const eventTypeId = rawEvent?.event_type_id;
  const roomTemplateId = rawEvent?.room_template_id;
  const graphicSetId = rawEvent?.graphic_set_id;
  const startedByUserId = rawEvent?.started_by_user_id;
  const finishedByUserId = rawEvent?.finished_by_user_id;
  const teamLogo1Id = rawEvent?.sport_event_data?.team_logo_1;
  const teamLogo2Id = rawEvent?.sport_event_data?.team_logo_2;

  useEffect(
    () => {
      let cancelled = false;
      fetchContract(contractId).then(data => !cancelled && setContract(data));
      return () => {
        cancelled = true;
      };
    },
    [contractId],
  );

  useEffect(
    () => {
      let cancelled = false;
      fetchDeployment(deploymentId).then(data => !cancelled && setDeployment(data));
      return () => {
        cancelled = true;
      };
    },
    [deploymentId],
  );

  useEffect(
    () => {
      let cancelled = false;
      fetchEventType(eventTypeId).then(data => !cancelled && setEventType(data));
      return () => {
        cancelled = true;
      };
    },
    [eventTypeId],
  );

  useEffect(
    () => {
      let cancelled = false;
      fetchRoomTemplate(contractId, roomTemplateId).then(data => !cancelled && setRoomTemplate(data));
      return () => {
        cancelled = true;
      };
    },
    [contractId, roomTemplateId],
  );

  useEffect(
    () => {
      let cancelled = false;
      fetchHasRoomTemplates(contractId).then(data => !cancelled && setHasRoomTemplates(data));
      return () => {
        cancelled = true;
      };
    },
    [contractId],
  );

  // startedByUser/finishedByUser, graphicSet, evu y los escudos de equipo no tienen endpoint
  // REST bajo /secure/ en el backend real que sirva para esto (confirmado con peticiones
  // directas: el único GET de usuario por /secure/ es por username, no por id interno), así
  // que se resuelven vía GraphQL.
  const eventUsersQuery = useMemo(() => getEventUsersQuery(eventId), [eventId]);
  const { data: eventUsersData } = useQuery(eventUsersQuery);
  const startedByUser = startedByUserId ? eventUsersData?.event?.startedByUser : null;
  const finishedByUser = finishedByUserId ? eventUsersData?.event?.finishedByUser : null;

  const graphicSetQuery = useMemo(() => getGraphicSetQueryWithAssets(graphicSetId, customerId), [
    graphicSetId,
    customerId,
  ]);
  const { data: graphicSetData } = useQuery(graphicSetQuery);
  const graphicSet = graphicSetData?.graphicset;

  const evuQuery = useMemo(
    () => {
      const query = getEventEvuQuery(eventId);
      return { ...query, skip: query.skip || !withEvu };
    },
    [eventId, withEvu],
  );
  const { data: evuData } = useQuery(evuQuery);
  const evu = evuData?.event?.evu;

  const asset1Query = useMemo(() => getAssetQuery(teamLogo1Id, customerId), [teamLogo1Id, customerId]);
  const { data: asset1Data } = useQuery(asset1Query);
  const teamLogoAsset1 = asset1Data?.asset;

  const asset2Query = useMemo(() => getAssetQuery(teamLogo2Id, customerId), [teamLogo2Id, customerId]);
  const { data: asset2Data } = useQuery(asset2Query);
  const teamLogoAsset2 = asset2Data?.asset;

  const event = useMemo(
    () =>
      mapEventFromRest(rawEvent, {
        contract,
        deployment,
        eventType,
        roomTemplate,
        hasRoomTemplates,
        startedByUser,
        finishedByUser,
        graphicSet,
        evu,
        teamLogoAsset1,
        teamLogoAsset2,
      }),
    [
      rawEvent,
      contract,
      deployment,
      eventType,
      roomTemplate,
      hasRoomTemplates,
      startedByUser,
      finishedByUser,
      graphicSet,
      evu,
      teamLogoAsset1,
      teamLogoAsset2,
    ],
  );

  return { event, loading, error, contract, deployment, refreshEvent };
};
